import { CloseCircleFilled, ExclamationCircleOutlined, RightOutlined } from '@ant-design/icons';
import posthog from '@leyan/lytics';
import { LocalMessage, getFailCauseMsg } from 'hooks/business/conversation';
import type { DialogueRefObject } from 'components/Dialogue/types';
import { css } from '@emotion/css';
import { eventBus } from 'services/eventBus';
import { AuthBeAboutToExpireFeature } from 'utils/featureFlag';
import { AuthFailMsg } from '../components/AuthInvalid/AuthFailMsg';
import ErrorReason from '../components/ErrorReason';
import { UIMessages } from '../useChatView';
import { failCauseCode } from '../constants';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ShopeeChatDiversion } from './MessageExtend/ShopeeChatDiversion';
import { Button } from 'antd';
import SellerCenter from 'components/SellerCenter';
import { getSellerCenterURL } from 'utils/sellerCenterURL';
import { useTranslate } from '@tolgee/react';

export const MessageStatusDecorate = ({
  message,
  status,
  content,
}: {
  message: LocalMessage;
  status?: UIMessages['status'];
  content: React.ReactNode;
  failCause?: Conversation.FailContent;
  dialogRef: DialogueRefObject | null;
}) => {
  if (message.localStatus === 'uploading') {
    return (
      <div className="relative max-w-[100%]" key="uploading">
        <div
          className={messageIcon}
          onClick={() => {
            eventBus.emit('CANCEL_SEND_MESSAGE', message);
            posthog.capture('$cancelSendMessage', { message });
          }}
        >
          <CloseCircleFilled />
        </div>
        {content}
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="relative max-w-[100%]" key="loading">
        {content}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <>
        <div className="relative max-w-[100%]">
          <div className={messageIcon}>
            <ExclamationCircleOutlined className="!text-sm" />
          </div>
          {content}
        </div>
      </>
    );
  }

  return (
    <div className="relative max-w-[100%]">
      <div className={messageIcon} />
      {content}
    </div>
  );
};

export function FailCauseContent({
  message,
  failCause,
}: {
  message: LocalMessage;
  failCause?: Conversation.FailContent;
}) {
  const { t } = useTranslate();

  const sysLang = useSystemLanguageValue();

  const msg = getFailCauseMsg(sysLang, failCause);

  if (failCauseCode.includes(failCause?.code) && AuthBeAboutToExpireFeature) {
    return <AuthFailMsg message={message} />;
  }

  if (failCause?.code === 'shop_bound_subaccount') {
    return <ShopeeChatDiversion message={message} />;
  }

  if (failCause?.code === 'need_verify_phone') {
    const { channel, storeId } = message;
    return (
      <div className="bg-[var(--fett-surface-error-bg)] rounded px-2 py-1 mt-1">
        {t('电话号码未验证，请前往卖家中心「店铺设置」完成电话号码验证后继续使用。')}
        <SellerCenter platform={channel}>
          <Button className="!p-0" type="link" onClick={() => getSellerCenterURL(channel, storeId)}>
            {t('ViewShops.shopee.multi.interrupt.modal.tasks.buttons.go.validate')}
            <RightOutlined />
          </Button>
        </SellerCenter>
      </div>
    );
  }

  return <ErrorReason msg={msg} message={message} reSendImage={failCause?.reSendImage} />;
}

const messageIcon = css`
  position: absolute;
  top: 50%;
  left: -8px;
  transform: translateX(-100%) translateY(-50%);
  .anticon-close-circle {
    cursor: pointer;
    color: var(--ant-error-color);
  }
  .anticon-exclamation-circle {
    color: var(--ant-error-color);
  }
`;
